// src/services/bookingWebSocket.js
/**
 * WebSocket service for real-time booking notifications
 * wss://aeroviewbackend.dockyardsoftware.com:8095/Booking
 *
 * When employee submits a booking, it sends to this WS
 * Driver dashboard listens to the same WS and displays popup in real time
 */

class BookingWebSocket {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 2000;
    this.authKey = null;
    this.driverSno = null;
    this.vehicleId = null;
    this.onBookingCallback = null;
    this.statusUpdateListeners = []; // 🔴 NEW: Array for multiple status listeners
    this.WS_URL = "wss://aeroviewbackend.dockyardsoftware.com:8095/Booking";
  }

  /**
   * Connect to booking WS server (for drivers to receive bookings)
   * @param {string} authKey - authentication token
   * @param {string} driverSno - driver's service number
   * @param {string} vehicleId - driver's assigned vehicle ID
   */
  connect(authKey, driverSno, vehicleId) {
    if (!authKey || !driverSno) {
      console.warn("[BookingWS] Missing authKey or driverSno");
      return;
    }

    this.authKey = authKey;
    this.driverSno = driverSno;
    this.vehicleId = vehicleId;

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      console.log("[BookingWS] Already connected or connecting");
      return;
    }

    this.disconnect();

    try {
      const url = this.WS_URL + "?token=" + encodeURIComponent(authKey);
      console.log("[BookingWS] Connecting to:", this.WS_URL);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.log(
          "[BookingWS] Connected! Listening for booking notifications",
        );

        // Send auth/subscribe message to register this driver
        try {
          this.ws.send(
            JSON.stringify({
              type: "subscribe",
              action: "driver_subscribe",
              token: authKey,
              driverSno: driverSno,
              vehicleId: vehicleId,
            }),
          );
          console.log("[BookingWS] Subscribed as driver:", driverSno);
        } catch (e) {
          console.error("[BookingWS] Subscribe send failed:", e);
        }
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          console.log("[BookingWS] Received message:", msg);

          // 🔴 NEW: Handle booking cancellation messages (from employee cancelling PENDING booking)
          // if (
          //   msg.type === "booking_cancelled" ||
          //   msg.action === "booking_cancelled"
          // ) {
          //   console.log(
          //     "[BookingWS] 📥 Booking cancellation received:",
          //     msg.bId,
          //   );
          //   if (this.onBookingCallback) {
          //     this.onBookingCallback(msg);
          //   }
          //   return; // Exit early, don't process as new booking
          // }

          if (
            msg.type === "booking_cancelled" ||
            msg.action === "booking_cancelled"
          ) {
            console.log(
              "[BookingWS] Booking cancellation received:",
              msg.bId,
            );
            if (this.onBookingCallback) {
              this.onBookingCallback(msg);
            }
            return;
          }

          // Check if this is a booking notification for this driver
          if (
            msg.type === "new_booking" ||
            msg.type === "booking" ||
            msg.dSNo ||
            msg.vId
          ) {
            // Only process if this booking is for this driver's vehicle
            if (
              msg.dSNo === this.driverSno ||
              msg.vId === this.vehicleId ||
              !msg.dSNo
            ) {
              console.log("[BookingWS] New booking for this driver:", msg);
              if (this.onBookingCallback) {
                this.onBookingCallback(msg);
              }
            }
          }
        } catch (e) {
          console.log("[BookingWS] Received (not JSON):", evt.data);
        }
      };

      this.ws.onerror = (err) => {
        console.error("[BookingWS] Error:", err);
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        console.log("[BookingWS] Closed");
        this.attemptReconnect();
      };
    } catch (err) {
      console.error("[BookingWS] Failed to create WebSocket:", err);
      this.attemptReconnect();
    }
  }

  /**
   * Send a booking notification (called by employee when submitting a booking)
   * @param {object} bookingData - the booking details
   */
  sendBooking(bookingData) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("[BookingWS] Cannot send booking - not connected");
      return false;
    }

    try {
      const message = {
        type: "new_booking",
        action: "create_booking",
        ...bookingData,
        timestamp: new Date().toISOString(),
      };
      this.ws.send(JSON.stringify(message));
      console.log("[BookingWS] Booking sent:", message);
      return true;
    } catch (e) {
      console.error("[BookingWS] Failed to send booking:", e);
      return false;
    }
  }

  /**
   * 🔴 NEW: Send booking status update (called by driver when changing status)
   * @param {object} statusData - { bId, status, dlatitude, dlongitude, vId, dSNo, ... }
   */
  sendStatusUpdate(statusData) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("[BookingWS] Cannot send status update - not connected");
      return false;
    }

    try {
      const message = {
        type: "booking_status_update",
        action: "update_status",
        ...statusData,
        timestamp: new Date().toISOString(),
      };
      this.ws.send(JSON.stringify(message));
      console.log("[BookingWS] 📤 Status update sent:", message);
      return true;
    } catch (e) {
      console.error("[BookingWS] Failed to send status update:", e);
      return false;
    }
  }

  /**
   * 🔴 NEW: Send booking cancellation message (called by employee when cancelling a pending booking)
   * @param {object} cancellationData - { bId, dSNo, vId, ... }
   */
  sendCancellation(cancellationData) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("[BookingWS] Cannot send cancellation - not connected");
      return false;
    }

    try {
      const message = {
        type: "booking_cancelled",
        action: "booking_cancelled",
        ...cancellationData,
        timestamp: new Date().toISOString(),
      };
      this.ws.send(JSON.stringify(message));
      console.log("[BookingWS] 📤 Cancellation sent:", message);
      return true;
    } catch (e) {
      console.error("[BookingWS] Failed to send cancellation:", e);
      return false;
    }
  }

  /**
   * Connect as employee to send bookings
   * @param {string} authKey - authentication token
   */
  connectAsEmployee(authKey) {
    if (!authKey) {
      console.warn("[BookingWS] Missing authKey for employee connection");
      return;
    }

    this.authKey = authKey;

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      console.log("[BookingWS] Already connected or connecting");
      return;
    }

    this.disconnect();

    try {
      const url = this.WS_URL + "?token=" + encodeURIComponent(authKey);
      console.log("[BookingWS] Employee connecting to:", this.WS_URL);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.log("[BookingWS] Employee connected! Ready to send bookings");

        // Send auth message
        try {
          this.ws.send(
            JSON.stringify({
              type: "auth",
              action: "employee_connect",
              token: authKey,
            }),
          );
        } catch (e) {
          console.error("[BookingWS] Auth send failed:", e);
        }
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          console.log("[BookingWS] Employee received message:", msg);

          // 🔴 NEW: Handle status update messages from backend
          // if (msg.type === "booking_status_update" || msg.type === "status_update" || (msg.bId && msg.status)) {
          //   console.log("[BookingWS] 📊 Status update message:", msg);
          // Handle status update / cancellation messages from backend
          if (
            msg.type === "booking_status_update" ||
            msg.type === "status_update" ||
            msg.type === "booking_cancelled" ||
            msg.action === "booking_cancelled" ||
            (msg.bId && msg.status)
          ) {
            console.log("[BookingWS] 📊 Status/cancellation message:", msg);
            // Notify all listeners
            this.statusUpdateListeners.forEach((listener) => {
              try {
                listener(msg);
              } catch (e) {
                console.error(e);
              }
            });
          }

          // Handle other booking messages
          if (
            msg.type === "new_booking" ||
            msg.type === "booking_accepted" ||
            msg.type === "booking_rejected"
          ) {
            if (this.onBookingCallback) {
              this.onBookingCallback(msg);
            }
          }
        } catch (e) {
          console.log("[BookingWS] Received (not JSON):", evt.data);
        }
      };

      this.ws.onerror = (err) => {
        console.error("[BookingWS] Error:", err);
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        console.log("[BookingWS] Employee connection closed");
      };
    } catch (err) {
      console.error("[BookingWS] Failed to create WebSocket:", err);
    }
  }

  /**
   * Set callback for when a new booking is received (for drivers)
   * @param {function} callback - function to call with booking data
   */
  onNewBooking(callback) {
    this.onBookingCallback = callback;
  }

  /**
   * 🔴 NEW: Add callback for status updates (supports multiple listeners)
   * @param {function} callback - function to call with status update data
   * @returns {function} unsubscribe function
   */
  onStatusUpdate(callback) {
    if (callback && typeof callback === "function") {
      this.statusUpdateListeners.push(callback);
      console.log(
        "[BookingWS] Added status listener. Total:",
        this.statusUpdateListeners.length,
      );
      return () => {
        this.statusUpdateListeners = this.statusUpdateListeners.filter(
          (cb) => cb !== callback,
        );
        console.log(
          "[BookingWS] Removed status listener. Total:",
          this.statusUpdateListeners.length,
        );
      };
    }
    // If null passed, clear all (legacy support, but discouraged)
    if (callback === null) {
      console.warn("[BookingWS] Clearing ALL status listeners via null");
      this.statusUpdateListeners = [];
    }
  }

  /**
   * Check if WebSocket is ready
   */
  isReady() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Attempt to reconnect after connection lost
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn("[BookingWS] Max reconnect attempts reached");
      return;
    }

    this.reconnectAttempts++;
    console.log(
      `[BookingWS] Reconnecting... attempt ${this.reconnectAttempts}`,
    );

    setTimeout(() => {
      if (this.driverSno) {
        this.connect(this.authKey, this.driverSno, this.vehicleId);
      } else {
        this.connectAsEmployee(this.authKey);
      }
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        console.error("[BookingWS] Error closing:", e);
      }
      this.ws = null;
    }
    this.isConnected = false;
    console.log("[BookingWS] Disconnected");
  }
}

// Export singleton instance
const bookingWS = new BookingWebSocket();
export default bookingWS;
